import React from "react";
import styled from "styled-components";
import classnames from "classnames";
import YearAmount from "../../basics/YearAmount";
import { TooltipTrigger } from '../../../Tooltip/Tooltip';
import { getPaymentCount } from "../../common/helpers";
import { AutomationPlacement } from "../../../../utilities/automation"
import useLanguage from "../../../../utilities/useLanguage";
import { getTranslation } from "../../translations";
import { createDataTestId, AutomationTestIdFeature, AutomationTestIdElementType } from "../../../../utilities/automation"

const RateName = styled(({ name, className = '', headerClassName, seeQuote }) => {
    return (
        <div className={classnames(className, "text-center bg-white p-3 py-4 border-bottom border-md-bottom-0 border-right border-md-right border-lg-bottom border-lg-right-0 border-xl-right border-xl-bottom-0 h-100")}>
            <h3 className="h2 text-muted font-weight-bold mb-0 rate-term">
                {name.header}
                <div className={`${headerClassName} font-weight-normal text-muted mb-0`}>
                    {name.subHeader}
                </div>
            </h3>
            <div className="see-quote">
                <a href="#" className="text-underline"><u>{seeQuote}</u></a>
            </div>
        </div>
    );
})<{ error: any }>`
    h3.rate-term {
        font-family: "National2", sans serif;

        div {
            font-family: "National2", sans serif;
            font-size: 1.33rem;
        }
    }

    div.see-quote {
        font-size: 0.778rem;
    }

    ${props => props.error.isError && `
        h3 {
            opacity: .2;
        }

        a {
            color: #6c757d;
            opacity: .2;
            pointer-events: none;
        }
    `}

    @media(min-width:769px) and (max-width:990px) {
        h3{
            font-size: 1.563rem !important;
        }

        a{
            font-size: 0.75rem;
        }
    }

    @media(min-width:1200px) and (max-width:1439px) {
        h3{
            font-size: 1.875rem !important;
        }

        .h2 {
            font-size: 1.5rem;
        }
    }
`;

export default styled(({ rate, onClick, className = '' }) => {
    const { lang } = useLanguage();

    const disclosureData = (lang === 'es' && rate.rate.product.disclosure.spanishDisclosureData) ? rate.rate.product.disclosure.spanishDisclosureData : rate.rate.product.disclosure.disclosureData

    function handleClick(event) {
        onClick(event, rate);
    }

    return (
        <div className={classnames(className, "col-12 row bg-gray-200")} onClick={!rate.error.isError ? handleClick : null}
            data-test-id={createDataTestId({feature: AutomationTestIdFeature.MRQT_CALCULATOR, element: AutomationTestIdElementType.COLLECTION_ITEM, specifier: 'rate_row'})}
            data-loan-balance={rate.rate.disclosureInformation.balance}
            data-loan-property-amount={rate.rate.disclosureInformation.propertyAmount}
            data-loan-name={rate.name}
            data-is-loan-enabled={rate.error.isError ? "false" : "true"}>
            <div className="col-12 col-md-5 col-lg-12 col-xl-5 p-0">
                <RateName name={rate.displayName} headerClassName={rate.headerClassName} error={rate.error} seeQuote={getTranslation(lang).detailedQuote} />
            </div>
            <div className="col-12 col-md-7 col-lg-12 col-xl-7 p-0 rate-amount border-bottom border-md-bottom-0">
                {rate.error.isError ?
                    <div className={classnames("h-100", "d-flex", "flex-column", "justify-content-center", "align-items-center", "bg-white", "text-primary p-3", "text-center")}>
                        <div className="h5 text-muted">{rate.error.message}</div>
                    </div> :
                    <YearAmount
                        monthlyPayment={rate.monthlyPayment}
                        apr={rate.rate.disclosureInformation.modifiedApr}
                        initialRate={rate.rate.modifiedInitialRate}
                        paymentCount={getPaymentCount(rate.rate.disclosureInformation)}
                        className="text-primary bg-white p-3 h-100"
                        percentageClassName="text-muted"
                        perMonthClassName="p-1 ml-1"
                        location={AutomationPlacement.CALCULATOR_PAGE}
                    />
                }
            </div>
            <TooltipTrigger className="d-md-block d-xl-block text-muted" html={disclosureData} place="left" />
        </div>
    );
})`
    cursor: pointer;
    position: relative;

    div.row {
        line-height: 1rem;
    }

    span.macu-tooltip-trigger {
        position: absolute;
        right: 1.25rem;
        top: 1.063rem;
        font-size: 1rem;
    }

    @media(min-width:769px) and (max-width:990px) {
        .rate-amount{
            background-color:white;
        }
    }

    @media(min-width:1200px) and (max-width:1439px) {
        .rate-amount {
            font-size: 2.375rem;
        }
    }

    ${props => !props.rate.error.isError &&
        `&: hover {
            ${YearAmount} {
                background: ${props.theme.primary} !important;
                color: white !important;

                .info {
                    color: white !important;
                }

                .text-muted {
                    color: white !important;
                }
            }

            ${RateName} {
                color: ${props.theme.primary} !important;

                .text-muted{
                    color: ${props.theme.primary} !important;
                }
            }
        }
    `}`;